// "Attach existing dealer" picker for the Manufacturing → Plants
// right pane. Mounted only when a real plant is selected (the
// Unattached pseudo-row doesn't have a primary plant to attach
// dealers to).
//
// Two sub-sections:
//   - Subscriber dealers not currently attached to this plant
//     (either unattached OR attached to a different plant).
//   - Non-app locations same shape.
// Picking either calls m.attachToActivePlant, which re-points
// primary_plant_id at the active plant. The picker auto-closes on
// success; surface-level errors still surface in the panel's
// localError banner.
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { UseManufacturingPlantDealersTab } from "./useManufacturingPlantDealersTab";

export function ManufacturingPlantAttachDealerPicker({
  m,
  activePlantLabel,
  onClose,
}: {
  m: UseManufacturingPlantDealersTab;
  activePlantLabel: string;
  onClose: () => void;
}) {
  const subs = m.attachableSubscribers;
  const locs = m.attachableLocations;
  const total = subs.length + locs.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-zinc-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-zinc-700">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
              Attach a dealer to {activePlantLabel}
            </h3>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">
              Picking a row re-points its primary plant at the active plant. Dealers attached to
              other plants will be re-assigned.
            </p>
          </div>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={onClose}
            disabled={m.actionBusy}
            aria-label="Close attach dealer picker"
          >
            Close
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-4 py-3 space-y-4">
          {total === 0 ? (
            <div className="text-sm text-gray-500 dark:text-zinc-400">
              Every dealer in this org is already attached to {activePlantLabel}.
            </div>
          ) : null}

          {subs.length > 0 ? (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                App-using dealers ({subs.length})
              </div>
              <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
                {subs.map((sub) => {
                  const currentPlant = sub.primary_plant_id
                    ? m.plantNameById.get(sub.primary_plant_id) ?? "(unknown plant)"
                    : "(unattached)";
                  return (
                    <li
                      key={sub.dealer_org_id}
                      className="flex items-center justify-between gap-2 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 dark:text-zinc-100">
                          {sub.dealer_org_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-zinc-400">
                          Current primary: {currentPlant}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={ghostBtnClass}
                        disabled={m.actionBusy}
                        onClick={() =>
                          void m.attachToActivePlant({
                            kind: "subscriber",
                            dealer_org_id: sub.dealer_org_id,
                          })
                        }
                      >
                        Attach
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {locs.length > 0 ? (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                Non-app dealer locations ({locs.length})
              </div>
              <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
                {locs.map((loc) => {
                  const currentPlant = loc.primary_plant_id
                    ? m.plantNameById.get(loc.primary_plant_id) ?? "(unknown plant)"
                    : "(unattached)";
                  return (
                    <li key={loc.id} className="flex items-center justify-between gap-2 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 dark:text-zinc-100">
                          {loc.name}
                          {!loc.is_active ? (
                            <span className="ml-2 rounded-md bg-gray-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                              Inactive
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-zinc-400">
                          Current primary: {currentPlant}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={ghostBtnClass}
                        disabled={m.actionBusy}
                        onClick={() =>
                          void m.attachToActivePlant({
                            kind: "location",
                            location_id: loc.id,
                          })
                        }
                      >
                        Attach
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
